// Boot helpers. The four entry points (`startNewGame`, `startSharedPuzzle`,
// `resumeActiveGame`, `replayFromRecord`) collapse the carve → createState →
// gameScene.mount → optional banner sequence into one function each so
// `main.ts` stays as glue rather than ceremony. Each helper assumes the host
// has already navigated into the game scene; they don't drive scene routing
// themselves.
//
// All "fresh" entry points (new, shared, replay) construct a brand-new
// `GameStateData` via `createState`; `GameScene.mount` calls
// `gameRecorder.start(state)` so any existing active session is overwritten.
// Callers that want a confirmation dialog before that happens should run it
// upstream and only invoke these helpers from the discard callback.
//
// The shared-puzzle banner is mounted here rather than inside `GameScene` so
// the scene constructor signature stays unchanged.

import { carvePuzzle } from '../generator/Carver.js';
import { createState } from '../game/GameState.js';
import { mountSharedPuzzleBanner } from '../ui/SharedPuzzleBanner.js';
import { ActiveGameStore, fromActiveSession } from '../history/ActiveGameStore.js';
import type { GameScene } from './GameScene.js';
import type { Difficulty } from '../game/types.js';
import type { GameRecord } from '../history/types.js';

const U32_MAX = 0xffffffff;

/** PRNG seed for a fresh "new puzzle" launch. Combines wall-clock + a u32 of
 *  randomness so two same-millisecond launches don't collide. */
export function freshSeed(): number {
  const t = Date.now() & U32_MAX;
  const r = Math.floor(Math.random() * (U32_MAX + 1));
  return (t ^ r) >>> 0;
}

export interface BootstrapDeps {
  readonly gameScene: GameScene;
}

export function startNewGame(deps: BootstrapDeps, difficulty: Difficulty): void {
  const puzzle = carvePuzzle(freshSeed(), difficulty);
  deps.gameScene.mount(createState(puzzle));
}

export function replayFromRecord(deps: BootstrapDeps, record: GameRecord): void {
  // Re-attempt is a fresh solve from the recorded seed — identical puzzle,
  // empty user grid, fresh stopwatch. We honor `record.difficulty` rather than
  // the user's default-difficulty setting so re-attempting Hard stays Hard.
  const puzzle = carvePuzzle(record.seed, record.difficulty);
  deps.gameScene.mount(createState(puzzle));
}

export interface SharedPuzzleArgs {
  readonly seed: number;
  readonly difficulty: Difficulty;
  readonly from: string | null;
  readonly timeMs: number | null;
}

export function startSharedPuzzle(deps: BootstrapDeps, args: SharedPuzzleArgs): void {
  const puzzle = carvePuzzle(args.seed, args.difficulty);
  deps.gameScene.mount(createState(puzzle));
  mountSharedPuzzleBanner({ from: args.from, timeMs: args.timeMs });
}

/**
 * Restore an in-progress puzzle from `ActiveGameStore`. Returns true iff a
 * session was found and the scene was mounted with it. Returns false when the
 * slot is empty so the host can fall through to the menu.
 *
 * GameScene.mount calls `gameRecorder.start(state)` — that rotates the session
 * id but preserves every field a re-saved snapshot needs (puzzle, user grid,
 * pencil, history, startedAt, hintsUsed, errorsMade), so the user keeps
 * solving where they left off and the recorded record on completion stays
 * accurate.
 */
export function resumeActiveGame(deps: BootstrapDeps): boolean {
  const session = ActiveGameStore.peek();
  if (session === null) return false;
  const state = fromActiveSession(session);
  deps.gameScene.mount(state);
  return true;
}
